import { useState } from "react";
import { Plus, Lock } from "lucide-react";
import { type User as UserEntity } from "wasp/entities";
import PlanSelectionModal from "../../../dashboard/PlanSelectionModal";
import LayoutLibraryModal from "../../../dashboard/widgets/LayoutLibraryModal";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import useTranslation from "../../hooks/useTranslation";
import { PaymentPlanId } from "../../../payment/plans";
import { cn } from "../../utils";

/**
 * "Add a page" entry for the two-level navigation.
 *
 * Mirrors the legacy SidebarAddPageItem: the button stays VISIBLE on Free and opens
 * the upgrade modal, rather than disappearing — a hidden feature can't be discovered.
 * Owning the modals here keeps SecondarySidebar a pure renderer of navConfig.
 */
export default function AddCustomPageButton({
  onCreatePage,
  user,
  className,
  isCollapsed = false,
}: {
  onCreatePage: (name: string, icon: string) => Promise<void>;
  user: Partial<UserEntity>;
  className?: string;
  /** Replie : icone seule + infobulle, comme les entrees du rail. Sans cela, la
   *  creation de page devient inaccessible tant que la sidebar est repliee. */
  isCollapsed?: boolean;
}) {
  const t = useTranslation();
  const [showLibrary, setShowLibrary] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);

  const plan = user.subscriptionPlan as PaymentPlanId | null | undefined;
  const isPro =
    plan === PaymentPlanId.Pro ||
    plan === PaymentPlanId.ProAnnual ||
    plan === PaymentPlanId.Max ||
    plan === PaymentPlanId.MaxAnnual;

  const button = (
    <button
      type="button"
      onClick={() => (isPro ? setShowLibrary(true) : setShowUpsell(true))}
      aria-label={t.newNav.addPage}
      className={cn(
        "flex items-center gap-2 rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
        isCollapsed
          ? "h-8 w-8 shrink-0 justify-center"
          : "w-full px-2 py-1.5 text-left text-xs",
        className
      )}
    >
      <Plus className="h-3.5 w-3.5 shrink-0" />
      {!isCollapsed && (
        <>
          <span className="flex-1 truncate">{t.newNav.addPage}</span>
          {!isPro && <Lock className="h-3 w-3 shrink-0 opacity-50" />}
        </>
      )}
    </button>
  );

  return (
    <>
      {isCollapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="right" className="whitespace-nowrap">
            {t.newNav.addPage}
          </TooltipContent>
        </Tooltip>
      ) : (
        button
      )}

      <PlanSelectionModal open={showUpsell} onOpenChange={setShowUpsell} />

      {showLibrary && (
        <LayoutLibraryModal
          open={showLibrary}
          onOpenChange={setShowLibrary}
          scope="custom-page"
          isFreePlan={false}
          onUpgradeClick={() => setShowUpsell(true)}
          mode="new-page"
          onCreateBlankPage={async () => {
            await onCreatePage(t.nav.addPage, "LayoutDashboard");
            setShowLibrary(false);
          }}
        />
      )}
    </>
  );
}
